import React from 'react';
import styled, { keyframes } from 'styled-components';
import SEO from '../../components/common/SEO';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaShoppingCart, FaTags, FaBoxes, FaSyncAlt, FaMoneyCheckAlt, FaBell,
  FaPuzzlePiece, FaSearchPlus, FaChartBar, FaGraduationCap, FaHeadset, FaRocket,
  FaCheckCircle, FaTimesCircle, FaArrowRight, FaHandshake
} from 'react-icons/fa';
import theme from '../../theme';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  color: ${theme.colors.white};
  padding: 7rem 0 5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(231, 76, 60, 0.08) 0%, transparent 50%);
    animation: ${keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`} 60s linear infinite;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 5%;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(231, 76, 60, 0.15);
  color: ${theme.colors.primary};
  padding: 0.5rem 1.25rem;
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(231, 76, 60, 0.3);
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 1.5rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.8);
  max-width: 650px;
  margin: 0 auto 2.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.05rem;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
`;

const ModelSection = styled.section`
  padding: 5rem 0;
  background: ${props => props.$dark ? '#f8f9fa' : theme.colors.white};
`;

const ModelInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 5%;
`;

const ModelTag = styled.span`
  display: inline-block;
  background: ${props => props.$color || theme.colors.primary};
  color: ${theme.colors.white};
  padding: 0.4rem 1rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 1rem;
`;

const ModelTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
`;

const ModelDescription = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${theme.colors.darkGray};
  margin-bottom: 1rem;
  max-width: 800px;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2.5rem;

  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.75rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
    border-color: ${theme.colors.primary};
  }
`;

const FeatureCardDark = styled(FeatureCard)`
  background: ${theme.colors.white};
  border-color: #e5e7eb;

  &:hover {
    border-color: #2563eb;
  }
`;

const FeatureIconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$bg || 'rgba(231, 76, 60, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  svg {
    font-size: 1.25rem;
    color: ${props => props.$color || theme.colors.primary};
  }
`;

const FeatureCardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
  margin-bottom: 0.5rem;
`;

const FeatureCardDesc = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${theme.colors.darkGray};
`;

const ComparisonSection = styled.section`
  padding: 5rem 0;
  background: ${theme.colors.white};
`;

const ComparisonInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 5%;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.secondary};
  margin-bottom: 0.75rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: ${theme.colors.darkGray};
  max-width: 600px;
  margin: 0 auto;
`;

const ComparisonTable = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const TableHeader = styled(TableRow)`
  background: ${theme.colors.secondary};
  color: ${theme.colors.white};
  font-weight: 700;
  font-size: 0.9rem;
`;

const TableCell = styled.div`
  padding: 1rem 1.25rem;
  font-size: 0.9rem;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.75rem 1rem;

    &:first-child {
      background: #f8f9fa;
      font-weight: 600;
    }
  }
`;

const TraditionalCell = styled(TableCell)`
  color: #6b7280;
  background: ${props => props.$odd ? '#fafafa' : theme.colors.white};

  svg {
    color: #d1d5db;
    flex-shrink: 0;
  }
`;

const PetekCell = styled(TableCell)`
  color: ${theme.colors.secondary};
  background: ${props => props.$odd ? 'rgba(231, 76, 60, 0.03)' : 'rgba(231, 76, 60, 0.06)'};
  font-weight: 500;

  svg {
    color: ${theme.colors.primary};
    flex-shrink: 0;
  }
`;

const ProcessSection = styled.section`
  padding: 5rem 0;
  background: #f8f9fa;
`;

const ProcessInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 5%;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-top: 3rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 36px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary});
    z-index: 0;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;

    &::before {
      display: none;
    }
  }
`;

const ProcessStep = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    align-items: flex-start;
    text-align: left;
    gap: 1rem;
  }
`;

const StepCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 auto 1rem;
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin: 0;
    min-width: 72px;
  }
`;

const StepContent = styled.div``;

const StepTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${theme.colors.secondary};
  margin-bottom: 0.4rem;
`;

const StepDesc = styled.p`
  font-size: 0.82rem;
  color: ${theme.colors.darkGray};
  line-height: 1.5;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #1a1a2e 100%);
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const CTAInner = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 5%;
  position: relative;
  z-index: 2;
`;

const CTAIcon = styled.div`
  font-size: 3rem;
  color: ${theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.white};
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-size: 1.05rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #c0392b;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(231, 76, 60, 0.4);
  }
`;

const b2bIcons = {
  1: <FaShoppingCart />,
  2: <FaTags />,
  3: <FaBoxes />,
  4: <FaSyncAlt />,
  5: <FaMoneyCheckAlt />,
  6: <FaBell />
};

const b4bIcons = {
  1: <FaPuzzlePiece />,
  2: <FaSearchPlus />,
  3: <FaChartBar />,
  4: <FaGraduationCap />,
  5: <FaHeadset />,
  6: <FaRocket />
};

const B2BB4BPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const stats = ['1', '2', '3', '4'];
  const featureKeys = ['1', '2', '3', '4', '5', '6'];
  const comparisonRows = ['1', '2', '3', '4', '5', '6'];
  const processSteps = ['1', '2', '3', '4', '5'];

  return (
    <>
      <SEO titleKey="b2bB4b.seo.title" descriptionKey="b2bB4b.seo.description" />

      <HeroSection>
        <HeroInner>
          <HeroTag>{t('b2bB4b.hero.tag')}</HeroTag>
          <HeroTitle>{t('b2bB4b.hero.title')}</HeroTitle>
          <HeroSubtitle>{t('b2bB4b.hero.subtitle')}</HeroSubtitle>
          <StatsRow>
            {stats.map(key => (
              <StatItem key={key}>
                <StatNumber>{t(`b2bB4b.stats.${key}.number`)}</StatNumber>
                <StatLabel>{t(`b2bB4b.stats.${key}.label`)}</StatLabel>
              </StatItem>
            ))}
          </StatsRow>
        </HeroInner>
      </HeroSection>

      <ModelSection>
        <ModelInner>
          <ModelTag>{t('b2bB4b.b2bSection.tag')}</ModelTag>
          <ModelTitle>{t('b2bB4b.b2bSection.title')}</ModelTitle>
          <ModelDescription>{t('b2bB4b.b2bSection.description1')}</ModelDescription>
          <ModelDescription>{t('b2bB4b.b2bSection.description2')}</ModelDescription>
          <FeaturesGrid>
            {featureKeys.map(key => (
              <FeatureCard key={key}>
                <FeatureIconWrap>
                  {b2bIcons[key]}
                </FeatureIconWrap>
                <FeatureCardTitle>{t(`b2bB4b.b2bSection.features.${key}.title`)}</FeatureCardTitle>
                <FeatureCardDesc>{t(`b2bB4b.b2bSection.features.${key}.description`)}</FeatureCardDesc>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </ModelInner>
      </ModelSection>

      <ModelSection $dark>
        <ModelInner>
          <ModelTag $color="#2563eb">{t('b2bB4b.b4bSection.tag')}</ModelTag>
          <ModelTitle>{t('b2bB4b.b4bSection.title')}</ModelTitle>
          <ModelDescription>{t('b2bB4b.b4bSection.description1')}</ModelDescription>
          <ModelDescription>{t('b2bB4b.b4bSection.description2')}</ModelDescription>
          <FeaturesGrid>
            {featureKeys.map(key => (
              <FeatureCardDark key={key}>
                <FeatureIconWrap $bg="rgba(37, 99, 235, 0.1)" $color="#2563eb">
                  {b4bIcons[key]}
                </FeatureIconWrap>
                <FeatureCardTitle>{t(`b2bB4b.b4bSection.features.${key}.title`)}</FeatureCardTitle>
                <FeatureCardDesc>{t(`b2bB4b.b4bSection.features.${key}.description`)}</FeatureCardDesc>
              </FeatureCardDark>
            ))}
          </FeaturesGrid>
        </ModelInner>
      </ModelSection>

      <ComparisonSection>
        <ComparisonInner>
          <SectionHeader>
            <SectionTitle>{t('b2bB4b.comparison.title')}</SectionTitle>
            <SectionSubtitle>{t('b2bB4b.comparison.subtitle')}</SectionSubtitle>
          </SectionHeader>
          <ComparisonTable>
            <TableHeader>
              <TableCell>{t('b2bB4b.comparison.headers.feature')}</TableCell>
              <TableCell>{t('b2bB4b.comparison.headers.traditional')}</TableCell>
              <TableCell>{t('b2bB4b.comparison.headers.petekb2b')}</TableCell>
            </TableHeader>
            {comparisonRows.map((key, i) => (
              <TableRow key={key}>
                <TableCell style={{ fontWeight: 600 }}>
                  {t(`b2bB4b.comparison.rows.${key}.feature`)}
                </TableCell>
                <TraditionalCell $odd={i % 2 === 0}>
                  <FaTimesCircle />
                  {t(`b2bB4b.comparison.rows.${key}.traditional`)}
                </TraditionalCell>
                <PetekCell $odd={i % 2 === 0}>
                  <FaCheckCircle />
                  {t(`b2bB4b.comparison.rows.${key}.petekb2b`)}
                </PetekCell>
              </TableRow>
            ))}
          </ComparisonTable>
        </ComparisonInner>
      </ComparisonSection>

      <ProcessSection>
        <ProcessInner>
          <SectionHeader>
            <SectionTitle>{t('b2bB4b.process.title')}</SectionTitle>
            <SectionSubtitle>{t('b2bB4b.process.subtitle')}</SectionSubtitle>
          </SectionHeader>
          <ProcessGrid>
            {processSteps.map(key => (
              <ProcessStep key={key}>
                <StepCircle>{key}</StepCircle>
                <StepContent>
                  <StepTitle>{t(`b2bB4b.process.steps.${key}.title`)}</StepTitle>
                  <StepDesc>{t(`b2bB4b.process.steps.${key}.description`)}</StepDesc>
                </StepContent>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </ProcessInner>
      </ProcessSection>

      <CTASection>
        <CTAInner>
          <CTAIcon><FaHandshake /></CTAIcon>
          <CTATitle>{t('b2bB4b.cta.title')}</CTATitle>
          <CTAText>{t('b2bB4b.cta.text')}</CTAText>
          <CTAButton to={`/${lang}/demo`}>
            {t('b2bB4b.cta.button')} <FaArrowRight style={{ marginLeft: 8, verticalAlign: 'middle' }} />
          </CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default B2BB4BPage;
